import {
  BlockConfig,
  zeroIOPS,
  OperationTypes,
  ContentTypes,
  Block,
  IOPS,
  multiplyIOPS,
  minIOPS,
} from "../block-base";
import { BlockTypes } from "../block-types";

export const cacheSideEffects = (block: Block, iops: IOPS): Block => {
  let finalBlock = { ...block };

  // Ensure dynamic properties exist
  let finalDynamicProperties = finalBlock.dynamicProperties || {};

  // Determine the configured max processing capacity
  const maxProcFromConfig =
    finalBlock.maxProcessingCapacity ||
    cacheConfig.maxProcessingCapacity ||
    cacheConfig.processingCapacity ||
    zeroIOPS();

  // Desired processing capacity should try to match the last seen input IOPS
  const desired = iops || zeroIOPS();

  // Compute the allowed processing capacity (element-wise min of desired and max)
  const allowed = minIOPS(desired, maxProcFromConfig);

  // Only increase processingCapacity (do not decrease automatically)
  const currentTotal = finalBlock.processingCapacity?.total || 0;
  const allowedTotal = allowed.total || 0;
  const maxTotal = maxProcFromConfig.total || 0;

  // If approaching max for the first time, set to max
  if (currentTotal >= maxTotal && maxTotal > 0 && finalDynamicProperties?.atMax !== true) {
    finalBlock.processingCapacity = maxProcFromConfig;
    finalDynamicProperties = { ...finalDynamicProperties, atMax: true };
  } else if (currentTotal >= maxTotal && maxTotal > 0 && finalDynamicProperties?.atMax === true) {
    // If already at max, randomly set to 75-100% of max on each call
    const multiplier = 0.75 + Math.random() * 0.25;
    finalBlock.processingCapacity = multiplyIOPS(maxProcFromConfig, multiplier);
    finalDynamicProperties = { ...finalDynamicProperties, atMax: false };
  } else if (allowedTotal > currentTotal) {
    finalBlock.processingCapacity = allowed;
  }

  finalBlock.dynamicProperties = finalDynamicProperties;
  finalBlock.maxProcessingCapacity = maxProcFromConfig;
  return finalBlock;
};

export const cacheConfig: BlockConfig = {
  displayName: "Cache",
  imagePath: "",
  isInitiator: true,
  isTerminator: false,
  isInterceptor: false,
  allowedInputTypes: new Set([
    BlockTypes.APP_SERVER,
    BlockTypes.AUTO_SCALING_GROUP,
    BlockTypes.LAMBDA,
    BlockTypes.NETWORK_LOAD_BALANCER,
  ]),
  allowedOutputTypes: new Set([
    BlockTypes.DATABASE,
    BlockTypes.DB_READ_REPLICA,
    BlockTypes.GRAPH_DB,
    BlockTypes.DOCUMENT_DB,
  ]),
  inputCapacity: {
    total: 150000,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 135000,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 10000,
          [`${ContentTypes.STATIC}`]: 80000,
          [`${ContentTypes.STATIC_MEDIA}`]: 45000,
        },
      },
      [`${OperationTypes.WRITE}`]: {
        total: 15000,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 10000,
          [`${ContentTypes.STATIC_MEDIA}`]: 5000,
        },
      },
    },
  },
  processingCapacity: zeroIOPS(), // processing starts at zero and will scale up via cacheSideEffects
  maxProcessingCapacity: {
    total: 100000,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 100000,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 20000,
          [`${ContentTypes.STATIC}`]: 80000,
        },
      },
    },
  },
  purchasePrice: 200,
  sellPrice: 100,
  costPerCycle: 1.0,
  outputConnectorLimits: {},
  blockSideEffects: (block: Block, iops: IOPS) => cacheSideEffects(block, iops),
};
